/**
 * IMPORTS
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import debounce from "lodash.debounce";
import { House, IdCard } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

// componenents
import { Button } from "@components/button/button";
import { MaskedInputCep } from "@components/masked-input-cep";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

// schema
import { getFormSchema } from "@pages/admin/editar-dados-cliente/enderecos/schema";

// hooks / react-query
import { useAtualizarEndereco } from "@/hooks/enderecos/atualizar";
import { IEnderecosPayloadAtualizar } from "@/hooks/enderecos/atualizar/index.d";
import { useBuscarPorEnderecosPorTipo } from "@/hooks/enderecos/buscar-por-enderecos-por-tipo";
import { useCriarEnderecoUsuario } from "@/hooks/enderecos/criar";

// utils
import { formatarTelefone } from "@/utils/format-telefone";

const notifyAlerta = (message: string) =>
  toast(message, {
    type: "warning",
    position: "top-center",
  });

const Enderecos = () => {
  const location = useLocation();
  const { cliente } = location.state || {};

  const [usarEnderecoEntrega, setUsarEnderecoEntrega] = useState(false);

  // configurando o useForm com a validação do Zod
  const formSchema = useMemo(() => getFormSchema(), []);

  const { criarEndrecoUsuarioAsync } = useCriarEnderecoUsuario();

  const { enderecosTipos } = useBuscarPorEnderecosPorTipo(cliente?.id);
  const { atualizarEnderecoAsync, isLoading } = useAtualizarEndereco();

  type FormData = z.infer<ReturnType<typeof getFormSchema>>;

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  // const { contatos, setContatos } = useStoreZustandManageClient();

  function formatCep(cep: string) {
    // Remove todos os caracteres que não são números
    cep = cep.replace(/\D/g, "");

    // Aplica a máscara XXXXX-XXX
    return cep.replace(/^(\d{5})(\d{3})$/, "$1-$2");
  }

  // Função de busca no ViaCEP com debounce para evitar requisições excessivas
  const fetchAddress = debounce(async (cep: string) => {
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      const { logradouro, bairro, localidade, uf } = response.data;

      if (logradouro) {
        setValue("endereco.cidade", localidade);
        setValue("endereco.estado", uf);
        setValue("endereco.bairro", bairro);
        setValue("endereco.cep", formatCep(cep));

        // Disparando a validação para os campos preenchidos automaticamente
        await trigger(["endereco.cidade", "endereco.estado", "endereco.bairro", "endereco.cep"]);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  }, 500);

  // Função de busca no ViaCEP com debounce para evitar requisições excessivas
  const buscarEnderecoDeEntregaViaCep = debounce(async (cep: string) => {
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      const { logradouro, bairro, localidade, uf } = response.data;

      if (logradouro) {
        setValue("enderecoEntrega.cidade", localidade);
        setValue("enderecoEntrega.estado", uf);
        setValue("enderecoEntrega.bairro", bairro);
        setValue("enderecoEntrega.cep", formatCep(cep));
        setValue("enderecoEntrega.logradouro", logradouro);

        // Disparando a validação para os campos preenchidos automaticamente
        await trigger([
          "enderecoEntrega.cidade",
          "enderecoEntrega.estado",
          "enderecoEntrega.bairro",
          "enderecoEntrega.cep",
        ]);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP ENDEREÇO ENTREGA:", error);
    }
  }, 500);

  // Atualiza o CEP e faz a busca quando o campo é preenchido
  const handleCepChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const cepValue = event.target.value.replace(/\D/g, "");

    if (cepValue.length === 8) {
      await fetchAddress(cepValue);
    }
  };

  const handleCepEntregaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const cepValueEntrega = event.target.value.replace(/\D/g, "");

    if (cepValueEntrega.length === 8) {
      await buscarEnderecoDeEntregaViaCep(cepValueEntrega);
    }
  };

  const handleCleanStateEnderecoDelivery = useCallback(() => {
    setValue("enderecoEntrega.cep", "");
    setValue("enderecoEntrega.estado", "");
    setValue("enderecoEntrega.cidade", "");
    setValue("enderecoEntrega.bairro", "");
    setValue("enderecoEntrega.numero", "");
    setValue("enderecoEntrega.complemento", "");
    setValue("enderecoEntrega.logradouro", "");
  }, [setValue]);

  // Função para lidar com o envio do formulário
  const onSubmit = useCallback(
    async (data: FormData) => {
      // dados de endereço do cliente
      const payload = {
        ...data?.endereco,
        user_id: cliente?.id,
        tipo_endereco: "residencial",
        nome_responsavel: data?.deliveryAddress?.name_address_delivery,
        email: data?.deliveryAddress?.email_address_delivery,
        setor: data?.deliveryAddress?.setor_address_delivery,
        telefone: data.deliveryAddress.phone_address_delivery,
      };

      if (usarEnderecoEntrega === true && !data.enderecoEntrega?.cep) {
        return notifyAlerta("Verifiques se os dados de entrega estão preenchidos tente novamente!");
      }

      const enderecoDelivery = {
        ...data.enderecoEntrega,
        user_id: cliente?.id,
        usar_mesmo_endereco_cadastrado_na_empresa: false,
        nome_responsavel: data?.deliveryAddress?.name_address_delivery,
        email: data?.deliveryAddress?.email_address_delivery,
        setor: data?.deliveryAddress?.setor_address_delivery,
        tipo_endereco: "entrega",
        telefone: data.deliveryAddress.phone_address_delivery,
      };

      const camposObrigatorios = [
        data.enderecoEntrega?.cep,
        data.enderecoEntrega?.estado,
        data.enderecoEntrega?.cidade,
        data.enderecoEntrega?.bairro,
        data.enderecoEntrega?.numero,
        data.enderecoEntrega?.logradouro,
      ];

      const hasEnderecoInvalido = camposObrigatorios.some(
        (campo) => campo === "" || campo === null || campo === undefined
      );

      // Resultado booleano
      const isInvalid = hasEnderecoInvalido; // true se houver erro

      if (usarEnderecoEntrega && isInvalid) {
        return notifyAlerta("Verifiques se os dados de entrega estão preenchidos tente novamente!");
      }

      await atualizarEnderecoAsync({
        endereco_id: enderecosTipos?.residencial.id!,
        payload: payload as IEnderecosPayloadAtualizar,
      });

      if (!isInvalid && !enderecosTipos?.entrega) {
        await criarEndrecoUsuarioAsync(enderecoDelivery as any);
      } else {
        if (!isInvalid && enderecosTipos?.entrega) {
          await atualizarEnderecoAsync({
            endereco_id: enderecosTipos?.entrega.id!,
            payload: enderecoDelivery as any,
          });
        }
      }
    },
    [usarEnderecoEntrega, atualizarEnderecoAsync, criarEndrecoUsuarioAsync, cliente, enderecosTipos]
  );

  useEffect(() => {
    if (enderecosTipos?.residencial) {
      setValue("endereco.cep", enderecosTipos?.residencial.cep || "");

      setValue("endereco.estado", enderecosTipos?.residencial.estado || "");
      setValue("endereco.cidade", enderecosTipos?.residencial.cidade || "");
      setValue("endereco.bairro", enderecosTipos?.residencial.bairro || "");
      setValue("endereco.numero", enderecosTipos?.residencial.numero || "");
      setValue("endereco.complemento", enderecosTipos?.residencial.complemento || "");

      setValue("endereco.logradouro", enderecosTipos.residencial?.logradouro);

      //
      setValue(
        "deliveryAddress.name_address_delivery",
        enderecosTipos?.residencial?.nome_responsavel
      );
      setValue("deliveryAddress.email_address_delivery", enderecosTipos?.residencial?.email);
      setValue(
        "deliveryAddress.phone_address_delivery",
        formatarTelefone(enderecosTipos?.residencial?.telefone)!
      );
      setValue("deliveryAddress.setor_address_delivery", enderecosTipos?.residencial?.setor);

      // Dispara validação dos campos preenchidos automaticamente
      trigger([
        "endereco.cep",
        "endereco.estado",
        "endereco.cidade",
        "endereco.bairro",
        "endereco.numero",
        "endereco.complemento",
        "endereco.logradouro",
      ]);
    }

    if (enderecosTipos?.entrega) {
      setValue("enderecoEntrega.cep", enderecosTipos?.entrega?.cep);
      setValue("enderecoEntrega.estado", enderecosTipos?.entrega?.estado);
      setValue("enderecoEntrega.cidade", enderecosTipos?.entrega?.cidade);
      setValue("enderecoEntrega.bairro", enderecosTipos?.entrega?.bairro);
      setValue("enderecoEntrega.numero", enderecosTipos?.entrega?.numero);
      setValue("enderecoEntrega.logradouro", "31748-540");
      setValue("enderecoEntrega.complemento", enderecosTipos?.entrega?.complemento);
      setUsarEnderecoEntrega(true);

      // Dispara validação dos campos preenchidos automaticamente
      trigger([
        "enderecoEntrega.cep",
        "enderecoEntrega.estado",
        "enderecoEntrega.cidade",
        "enderecoEntrega.bairro",
        "enderecoEntrega.numero",
        "enderecoEntrega.complemento",
        "enderecoEntrega.logradouro",
      ]);
    }
  }, [enderecosTipos, setValue, trigger]);

  return (
    <div className="flex w-full justify-center px-4">
      <div className="w-full max-w-5xl rounded-lg bg-white p-6 shadow-md">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Seção de Endereço */}
          <div className="w-ful">
            <div className="mb-4 mt-4 flex w-full items-center justify-start gap-2">
              <House className="text-zinc-800" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">Endereço da Empresa</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">CEP</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />

                  <MaskedInputCep
                    {...register("endereco.cep")}
                    onChange={handleCepChange}
                    placeholder="Informe o CEP"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.cep && (
                  <span className="text-sm text-red-500">{errors.endereco.cep.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Estado</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.estado")}
                    placeholder="Informe o Estado"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.estado && (
                  <span className="text-sm text-red-500">{errors.endereco.estado.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Cidade</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.cidade")}
                    placeholder="Informe a Cidade"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.cidade && (
                  <span className="text-sm text-red-500">{errors.endereco.cidade.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Bairro</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.bairro")}
                    placeholder="Informe o Bairro"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.bairro && (
                  <span className="text-sm text-red-500">{errors.endereco.bairro.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Número</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.numero")}
                    placeholder="Informe o Número"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.numero && (
                  <span className="text-sm text-red-500">{errors.endereco.numero.message}</span>
                )}
              </div>

              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Complemento</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("endereco.complemento")}
                    placeholder="Informe o Complemento"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.endereco?.complemento && (
                  <span className="text-sm text-red-500">
                    {errors.endereco.complemento.message}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 flex-col">
              <p className="mb-2 text-zinc-800">Logradouro</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("endereco.logradouro")}
                  placeholder="Informe o endereço ex: Rua A / Avenida B"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.endereco?.logradouro && (
                <span className="text-sm text-red-500">{errors.endereco.logradouro.message}</span>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="balcao"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={usarEnderecoEntrega}
              onChange={() => {
                if (usarEnderecoEntrega) {
                  handleCleanStateEnderecoDelivery();
                }
                setUsarEnderecoEntrega(!usarEnderecoEntrega);
              }}
            />
            <label htmlFor="balcao" className="ml-2 text-red-400">
              Alterar endereço da entrega
            </label>
          </div>

          <div className="mt-2 flex-row items-center rounded-md">
            {usarEnderecoEntrega && (
              <>
                <div className="mb-4 mt-4 flex w-full items-center justify-start gap-2">
                  <House className="text-zinc-800" size={24} />
                  <h2 className="text-xl font-semibold text-zinc-800">Endereço da Entrega</h2>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">CEP</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <MaskedInputCep
                        {...register("enderecoEntrega.cep")}
                        placeholder="Informe o CEP"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                        onChange={handleCepEntregaChange}
                      />
                    </div>
                    {errors.enderecoEntrega?.cep && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega?.cep.message}
                      </span>
                    )}
                  </div>

                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">Estado</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <input
                        {...register("enderecoEntrega.estado")}
                        placeholder="Informe o Estado"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                      />
                    </div>
                    {errors.enderecoEntrega?.estado && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega.estado.message}
                      </span>
                    )}
                  </div>

                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">Cidade</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <input
                        {...register("enderecoEntrega.cidade")}
                        placeholder="Informe a Cidade"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                      />
                    </div>
                    {errors.enderecoEntrega?.cidade && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega.cidade.message}
                      </span>
                    )}
                  </div>

                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">Bairro</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <input
                        {...register("enderecoEntrega.bairro")}
                        placeholder="Informe o Bairro"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                      />
                    </div>
                    {errors.enderecoEntrega?.bairro && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega.bairro.message}
                      </span>
                    )}
                  </div>

                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">Número</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <input
                        {...register("enderecoEntrega.numero")}
                        placeholder="Informe o Número"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                      />
                    </div>
                    {errors.enderecoEntrega?.numero && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega.numero.message}
                      </span>
                    )}
                  </div>

                  <div className="flex-col">
                    <p className="mb-2 text-zinc-800">Complemento</p>
                    <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                      <IdCard className="size-5 text-zinc-400" />
                      <input
                        {...register("enderecoEntrega.complemento")}
                        placeholder="Informe o Complemento"
                        className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                      />
                    </div>
                    {errors.enderecoEntrega?.complemento && (
                      <span className="text-sm text-red-500">
                        {errors.enderecoEntrega.complemento.message}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {usarEnderecoEntrega && (
            <div className="flex-col">
              <p className="mb-2 text-zinc-800">Logradouro</p>
              <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                <IdCard className="size-5 text-zinc-400" />
                <input
                  {...register("enderecoEntrega.logradouro")}
                  placeholder="Informe o endereço ex: Rua A / Avenida B"
                  className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                />
              </div>
              {errors.enderecoEntrega?.logradouro && (
                <span className="text-sm text-red-500">
                  {errors.enderecoEntrega.logradouro.message}
                </span>
              )}
            </div>
          )}

          {/**SEÇÃO DO RESPONSAVEL PELO ENDEREÇO */}
          <div className="w-full">
            <div className="mb-4 grid grid-cols-2 gap-4">
              {/* NOME DE IDENTIFICACAO */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Nome do Responsável</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.name_address_delivery")}
                    placeholder="Informe o Nome do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.name_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.name_address_delivery.message}
                  </span>
                )}
              </div>
              {/* E-MAIL */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">E-mail</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.email_address_delivery")}
                    placeholder="Informe o E-mail do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.email_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress.email_address_delivery?.message}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* SETOR */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Setor</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.setor_address_delivery")}
                    placeholder="Informe o Setor do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.setor_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.setor_address_delivery.message}
                  </span>
                )}
              </div>

              {/* TELEFONE */}
              <div className="flex-col">
                <p className="mb-2 text-zinc-800">Telefone</p>
                <div className="flex h-14 items-center gap-2 rounded-lg border border-zinc-800 px-4">
                  <IdCard className="size-5 text-zinc-400" />
                  <input
                    {...register("deliveryAddress.phone_address_delivery")}
                    placeholder="Informe o Telefone do Responsável"
                    className="flex-1 bg-transparent text-lg text-zinc-600 placeholder-zinc-400 outline-none"
                  />
                </div>
                {errors.deliveryAddress?.phone_address_delivery && (
                  <span className="text-sm text-red-500">
                    {errors.deliveryAddress?.phone_address_delivery.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Button disabled={isLoading} type="submit" className="mt-6 w-full ring-offset-background">
            {isLoading ? <SpinnerSimple /> : <>Alterar informações</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

/**
 * EXPORTS
 *
 */
export { Enderecos };
